import React, {
  FC,
  useState,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Divider,
  IconButton,
  ScrollView,
  CloseIcon,
  Pressable,
} from "native-base";
import { useWindowDimension } from "../../../Utility/WindowDimensions";
import { useSelector, RootStateOrAny, useDispatch } from "react-redux";
import { setEntitydata } from "../../../Redux/Actions/userActions";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { Loc } from "../../../App/Loc";
import * as AV from "../../../AVCore/AVCore";
import EntityProfilePic from "../../common/EntityProfilePic";
import {
  AddIcon,
  SearchIcon,
  TrashIcon,
  TickCircle,
  TickCircleSolid,
} from "../../../assets/icons/icon-assets";
import AVCancel from "../../../components/common/AVCancel";
import AVInputtext from "../../../components/AVInputtext";
import AVConfirmationModal from "../../../components/common/AVConfirmationModal";

/* ----------------------------------
   Types
---------------------------------- */

type ExtendedEntity = AV.IAVEntity | AV.IEvent | AV.ICollect | AV.IOrgBase;

interface Props {
  entity: ExtendedEntity;
  setEntity: Dispatch<SetStateAction<ExtendedEntity | undefined>>;
  isAdminView: boolean;
  isOngoingOrFuture: boolean;
  eventSeriesId?: string | null | undefined;
  handleProfileType: (type: "Member") => void;
}

/* ----------------------------------
   Main Component
---------------------------------- */

export const AVFeeCollectMembers: FC<Props> = ({
  entity,
  setEntity,
  isAdminView,
  eventSeriesId,
  isOngoingOrFuture,
  handleProfileType,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dimensions = useWindowDimension();
  const isMobile = dimensions?.width < 600;

  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user?.[0] ?? null,
  );

  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist ?? [],
  );

  const isOnline = useSelector((state: RootStateOrAny) => state.user.isOnline);

  const [searchInput, setSearchInput] = useState("");
  const [isEventCanceled, setIsEventCanceled] = useState(false);

  /** source of truth */
  const [allMembers, setAllMembers] = useState<AV.ICollectLink[]>(
    (entity as AV.ICollect)?.participants ?? [],
  );
  const [feeCollectMembers, setFeeCollectMembers] = useState<AV.ICollectLink[]>(
    (entity as AV.ICollect)?.participants ?? [],
  );

  /* ----------------------------------
     Handlers
  ---------------------------------- */

  const checkExpired = (date?: string | Date): boolean => {
    if (!date) return false;

    const targetDate = typeof date === "string" ? new Date(date) : date;
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays < 0;
  };

  const checkEventCanceled = async (): Promise<boolean> => {
    if (entity?.parent?.type !== "Event" && entity?.parent?.type !== "Game") {
      return false;
    }

    let evtEntity =
      entityDataList?.find((x) => x?.id === entity?.parent?.id) ?? null;

    if (!evtEntity) {
      const evtEntityAPI: AV.IAVEntity | null = await AV.AVEntity.get(
        entity?.parent,
      );

      if (evtEntityAPI) {
        entityDataList?.push(evtEntityAPI);
        dispatch(setEntitydata(entityDataList));
        evtEntity = evtEntityAPI;
      }
    }

    // Check main event
    if (evtEntity?.status === "Canceled") {
      return true;
    }

    // Check series event
    if (
      evtEntity?.seriesEvents !== undefined &&
      evtEntity?.seriesEvents?.length > 0
    ) {
      const currentEvent = Array.isArray(evtEntity?.seriesEvents)
        ? evtEntity.seriesEvents.find((x) => x.id === eventSeriesId)
        : null;

      const currentEventObj = currentEvent || evtEntity;

      return currentEventObj?.status === "Canceled";
    }

    return false;
  };

  const handleSearchChange = (val: string) => {
    setSearchInput(val);
    const text = val.trim().toLowerCase();

    if (!text) {
      setFeeCollectMembers(allMembers);
      return;
    }

    setFeeCollectMembers(
      allMembers.filter(
        (x) =>
          x?.title?.toLowerCase().includes(text) ||
          x?.data?.toLowerCase().includes(text),
      ),
    );
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setFeeCollectMembers(allMembers);
  };

  useEffect(() => {
    const loadData = async () => {
      const orgEntity = entity as AV.ICollect;
      if (orgEntity?.participants) {
        setAllMembers(orgEntity.participants);
        setFeeCollectMembers(orgEntity.participants);
      }

      const isCanceled = await checkEventCanceled();
      setIsEventCanceled(isCanceled);
    };

    loadData();
  }, [entity]);

  /* ----------------------------------
     Render
  ---------------------------------- */

  return (
    <Box
      minHeight={"340px"}
      borderRadius={5}
      backgroundColor={"AVColor.white"}
      shadow={1}
      flex={1}
      mb={3}
    >
      <HStack
        flexWrap="wrap"
        zIndex={-3}
        alignItems="flex-start"
        space={3}
        p={6}
      >
        {/*{allMembers?.length > 0 ? (*/}
        <VStack space={4} w="100%">
          <HStack justifyContent="space-between" alignItems="center">
            <Text fontSize="20px" fontWeight="500" color="AVColor.primary">
              {Loc.currentLang.feeCollect.members}
            </Text>

            <HStack space={4} alignItems="center">
              {allMembers?.length > 0 && (
                <FeeCollectSearchBox
                  searchInput={searchInput}
                  onSearchChange={handleSearchChange}
                  onClearSearch={handleClearSearch}
                  isMobile={isMobile}
                />
              )}

              {isAdminView &&
                !checkExpired((entity as AV.ICollect)?.end) &&
                //isOngoingOrFuture &&
                !isEventCanceled && (
                  <Button
                    isDisabled={!isOnline}
                    rounded="full"
                    bgColor="gray.100"
                    _hover={{ bgColor: "gray.50" }}
                    shadow={1}
                    p="10px"
                    top="-14px"
                    onPress={() => handleProfileType("Member")}
                  >
                    <AddIcon size="20px" color="#32AE78" />
                  </Button>
                )}
            </HStack>
          </HStack>

          {!feeCollectMembers || feeCollectMembers.length === 0 ? (
            <Box py={4} alignItems="center">
              <Text color="coolGray.400">
                {Loc.currentLang.feeCollect.noMemberFound}
              </Text>
            </Box>
          ) : (
            <FeeCollectMemberList
              entity={entity}
              setEntity={setEntity}
              setAllMembers={setAllMembers}
              feeCollectMembers={feeCollectMembers}
              setFeeCollectMembers={setFeeCollectMembers}
              navigate={navigate}
              currentUser={currentUser}
              entityDataList={entityDataList}
              dimensions={dimensions}
              isMobile={isMobile}
              isEventCanceled={isEventCanceled}
              isOngoingOrFuture={isOngoingOrFuture}
            />
          )}
        </VStack>
        {/*) : (*/}
        {/*  <HStack alignItems="center" space={2}>*/}
        {/*    <img src={inform} width="20" height="20" />*/}
        {/*    <Text>{"helptext"}</Text>*/}
        {/*  </HStack>*/}
        {/*)}*/}
      </HStack>
    </Box>
  );
};

export default AVFeeCollectMembers;

/* ----------------------------------
   Search Box
---------------------------------- */

const FeeCollectSearchBox: FC<{
  searchInput: string;
  onSearchChange: (val: string) => void;
  onClearSearch: () => void;
  isMobile: boolean;
}> = ({ searchInput, onSearchChange, onClearSearch, isMobile }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Box mb={6} position="relative" zIndex={10}>
      <HStack space={2} alignItems="center">
        <Input
          ref={inputRef}
          width={isMobile ? "200px" : "260px"}
          height="36px"
          overflow="hidden"
          value={searchInput}
          placeholder={Loc.currentLang.feeCollect.search}
          variant="AVSearchInput"
          placeholderTextColor="AVColor.textLite"
          backgroundColor="#F5F5F5"
          borderRadius="22px"
          borderWidth={0}
          fontSize="14px"
          InputLeftElement={
            <SearchIcon ml="2" size="24px" color="#000" left="10px" p="7px" />
          }
          InputRightElement={
            searchInput ? (
              <IconButton
                icon={<CloseIcon size="xs" />}
                size="sm"
                variant="AVSearchInput"
                onPress={() => {
                  onClearSearch();
                  inputRef.current?.focus();
                }}
                _hover={{ bg: "transparent" }}
              />
            ) : undefined
          }
          onChangeText={(val) => {
            onSearchChange(val);
            inputRef.current?.focus();
          }}
        />
      </HStack>
    </Box>
  );
};

/* ----------------------------------
   Members List
---------------------------------- */

const FeeCollectMemberList: FC<{
  entity: ExtendedEntity;
  setEntity: Dispatch<SetStateAction<ExtendedEntity | undefined>>;
  setAllMembers: Dispatch<SetStateAction<AV.ICollectLink[]>>;
  feeCollectMembers: AV.ICollectLink[];
  setFeeCollectMembers: Dispatch<SetStateAction<AV.ICollectLink[]>>;
  navigate: NavigateFunction;
  currentUser: AV.IAVEntity | null;
  entityDataList: AV.IAVEntity[];
  dimensions: { width: number; height: number };
  isMobile: boolean;
  isEventCanceled?: boolean;
  isOngoingOrFuture?: boolean;
}> = ({
  entity,
  setEntity,
  setAllMembers,
  feeCollectMembers,
  setFeeCollectMembers,
  navigate,
  currentUser,
  entityDataList,
  dimensions,
  isMobile,
  isEventCanceled,
  isOngoingOrFuture,
}) => {
  const [payAmount, setPayAmount] = useState("");
  const [isAPICall, setIsAPICall] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [removedItem, setRemovedItem] = useState<AV.ICollectLink | null>(null);

  const [isShowPayPopup, setIsShowPayPopup] = useState(false);
  const [selectedPayItem, setSelectedPayItem] =
    useState<AV.ICollectLink | null>(null);

  const remainingAmount =
    (selectedPayItem?.goalAmount ?? 0) - (selectedPayItem?.amount ?? 0);

  const enteredAmount = Number(payAmount);

  const handleRemoveItem = (item: AV.ICollectLink) => {
    setIsConfirm(true);
    setRemovedItem(item);
  };

  const handleRemoveMember = async () => {
    if (!removedItem) return;

    setIsAPICall(true);

    try {
      const removeMember = await AV.Collect.removeParticipant(
        AV.AVEntity.getFk(entity),
        removedItem as AV.ILink,
      );

      if (removeMember) {
        setEntity?.(removeMember);
        setAllMembers(removeMember?.participants ?? []);
        setFeeCollectMembers(removeMember?.participants ?? []);
      }
    } catch (error) {
      console.error("Failed to remove member:", error);
    } finally {
      setIsAPICall(false);
      setIsConfirm(false);
      setRemovedItem(null);
    }
  };

  const handleAddPayment = (item: AV.ICollectLink) => {
    setSelectedPayItem(item);
    setIsShowPayPopup(true);
    const remaining = (item?.goalAmount ?? 0) - (item?.amount ?? 0);
    setPayAmount(String(Math.max(remaining, 0)));
  };

  const handleClosePayPopup = () => {
    setIsShowPayPopup(false);
    //setPayAmount("");
    setSelectedPayItem(null);
  };

  const handleCollectPay = async () => {
    if (
      isAPICall ||
      !selectedPayItem ||
      enteredAmount <= 0 ||
      enteredAmount > remainingAmount
    ) {
      return;
    }

    try {
      setIsAPICall(true);

      const sourceFK: AV.ILink = {
        id: entity?.id,
        pk: entity?.pk,
        region: entity?.region,
        type: entity?.type,
      };

      const participantLink: AV.ILink = {
        id: selectedPayItem?.id,
        pk: selectedPayItem?.pk,
        region: selectedPayItem?.region,
        type: selectedPayItem?.type,
      };

      const donationLink: AV.ILink & { amount: number } = {
        //...participantLink,
        id: crypto.randomUUID(),
        amount: parseFloat(payAmount.replace("$", "")) ?? 0,
      };

      const updatedCollect = await AV.Collect.editDonation(
        sourceFK,
        participantLink,
        donationLink,
      );

      if (updatedCollect) {
        setPayAmount("");
        setSelectedPayItem(null);
        setIsShowPayPopup(false);
        setEntity?.(updatedCollect);
        setFeeCollectMembers(updatedCollect?.participants ?? []);
      }
    } catch (error) {
      console.error("Error during collect pay action:", error);
    } finally {
      setIsAPICall(false);
    }
  };

  return (
    <>
      <ScrollView maxHeight={260}>
        <VStack space={3}>
          {feeCollectMembers.map((item) => (
            <HStack
              key={item.id}
              alignItems="center"
              justifyContent="space-between"
              borderBottomWidth={1}
              borderColor="coolGray.100"
              pb={2}
            >
              {/* Left Section: Avatar + Name */}
              <HStack alignItems="center" space={3} flex={1}>
                <Box
                  size={8}
                  borderRadius="full"
                  bg="coolGray.200"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Pressable
                    position={"absolute"}
                    onPress={() => {
                      switch (item?.type) {
                        case "Person":
                        case "Pet":
                          navigate(`/profile/${item?.id}/${item?.region}`, {
                            state: {
                              user: {
                                id: item?.id,
                                pk: item?.pk,
                                region: item?.region,
                                type: item?.type,
                                name: item?.title,
                              },
                            },
                          });
                          break;
                        default:
                          navigate(`/teams/${item?.id}/${item?.region}`, {
                            state: { isCreate: false, team: item },
                          });
                          break;
                      }
                    }}
                  >
                    <EntityProfilePic
                      width="32px"
                      height="32px"
                      link={
                        currentUser && currentUser.id === item?.id
                          ? AV.AVEntity.getLink(currentUser)
                          : (() => {
                              const entity = entityDataList?.find(
                                (x) => x?.id === item?.id,
                              );
                              return entity
                                ? AV.AVEntity.getLink(entity)
                                : item;
                            })()
                      }
                    />
                  </Pressable>
                </Box>

                <VStack>
                  <Text fontSize="sm" fontWeight="medium">
                    {item.data || item.title}
                  </Text>
                </VStack>
              </HStack>

              {/* Right Section: Payment + Amount + Payment Status + Remove */}
              <HStack
                alignItems="center"
                justifyContent="flex-end"
                space={4}
                minW="380px"
              >
                {/* Add Payment */}
                {(entity as AV.ICollect)?.goal !== item.amount && (
                  <Button
                    size="sm"
                    rounded="full"
                    backgroundColor="AVColor.primary"
                    minW="112px"
                    height="32px"
                    _text={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "white",
                    }}
                    _hover={{ opacity: 0.9 }}
                    _pressed={{ opacity: 0.85 }}
                    onPress={() => handleAddPayment(item)}
                  >
                    {Loc.currentLang.feeCollect.addPaymentCaps}
                  </Button>
                )}

                {/* Amount */}
                <Box minW="90px" alignItems="center">
                  <Text fontSize="13px" fontWeight="600">
                    <Text color="#00A7BC">${item.amount ?? 0}</Text>
                    <Text color="#00000080"> / ${item.goalAmount ?? 0}</Text>
                  </Text>
                </Box>

                {/* Payment Status */}
                <Box width="32px" alignItems="center">
                  {(entity as AV.ICollect)?.goal === item.amount ? (
                    <TickCircle width="24px" height="24px" />
                  ) : (
                    <TickCircleSolid width="24px" height="24px" />
                  )}
                </Box>

                {/* Remove */}
                <Box width="32px" alignItems="center">
                  {(entity as AV.ICollect)?.goal !== item.amount &&
                  //isOngoingOrFuture &&
                  !isEventCanceled ? (
                    <Pressable
                      onPress={() => handleRemoveItem(item)}
                      borderRadius="full"
                      p="6px"
                      _hover={{ bg: "red.50" }}
                      _pressed={{ bg: "red.100" }}
                    >
                      <TrashIcon width="24px" height="24px" />
                    </Pressable>
                  ) : null}
                </Box>
              </HStack>
            </HStack>
          ))}
        </VStack>
      </ScrollView>

      {isShowPayPopup && (
        <Box
          position="fixed"
          bgColor="AVColor.overlay2"
          top={0}
          bottom={0}
          left={0}
          right={0}
          zIndex={99999}
        >
          <div
            className={`create_post_popup ${
              isMobile && "create_post_popup_mobile"
            }`}
            style={{
              width: isMobile ? dimensions.width - 15 : "565px",
              height: isMobile ? "650px" : "280px",
              marginTop: "12px",
            }}
          >
            <VStack width="100%" padding="24px" space={2}>
              {/* Header */}
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontSize="24px" fontWeight="600">
                  {Loc.currentLang.feeCollect.manualPay}
                </Text>

                <AVCancel
                  callback={handleClosePayPopup}
                  color={undefined}
                  size={undefined}
                />
              </HStack>

              {/* Description */}
              <Text fontSize="16px" color="coolGray.700">
                {Loc.currentLang.feeCollect.directlyToYouBy}{" "}
                <Text fontSize="16px" fontWeight="bold">
                  {selectedPayItem?.title ?? "member"}
                </Text>
              </Text>

              {/* Remaining Amount */}
              <Text fontSize="16px" color="#32AE78">
                {`${Loc.currentLang.feeCollect.remainingAmount}: $${remainingAmount}`}
              </Text>

              {/* Input */}
              <Box width="95%" top="6px">
                <AVInputtext
                  type="text"
                  label={Loc.currentLang.feeCollect.enterAmount}
                  value={payAmount}
                  maxLength={7}
                  onChange={(e) => {
                    let input = e.target.value;

                    // Allow numbers and a single decimal point
                    input = input
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*)\./g, "$1");

                    // Limit to 2 decimal places
                    const parts = input.split(".");
                    if (parts[1]?.length > 2) {
                      input = `${parts[0]}.${parts[1].slice(0, 2)}`;
                    }

                    setPayAmount(input);
                  }}
                />
              </Box>

              {/* Action Button */}
              <HStack justifyContent="center" pt={2}>
                <Button
                  width="180px"
                  height="40px"
                  rounded="full"
                  backgroundColor="AVColor.primary"
                  isDisabled={
                    isAPICall ||
                    !payAmount?.trim() ||
                    enteredAmount <= 0 ||
                    enteredAmount > remainingAmount
                  }
                  isLoading={isAPICall}
                  onPress={handleCollectPay}
                >
                  <Text color="white" fontWeight="500">
                    {Loc.currentLang.feeCollect.doneCaps}
                  </Text>
                </Button>
              </HStack>
            </VStack>
          </div>
        </Box>
      )}

      {isConfirm && (
        <AVConfirmationModal
          showconfirmationPopup={isConfirm}
          setshowconfirmationPopup={setIsConfirm}
          Confirmationtext={Loc.currentLang.feeCollect.removeConfirm}
          type={"RemoveCollectMember"}
          item={undefined}
          function={handleRemoveMember}
          isLoader={isAPICall}
        />
      )}
    </>
  );
};
